import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { isSocialPlatform, SocialPlatform } from '../types';

export const connectResults = ['connected', 'pickTarget', 'error'] as const;
export type ConnectResult = (typeof connectResults)[number];

export const connectReasons = [
  'denied',
  'invalidState',
  'exchangeFailed',
  'noTargets',
  // The login through a Facebook Page found no Instagram account. The banner offers the
  // sign-in of Instagram itself for it.
  'noInstagramAccount',
  // Meta has not reviewed the app for the platform, and the user is not a tester of the app.
  'notReviewed',
  'notConfigured',
  'unknown',
] as const;
export type ConnectReason = (typeof connectReasons)[number];

export interface ConnectOutcome {
  platform: SocialPlatform;
  result: ConnectResult;
  reason: ConnectReason;
}

function toResult(value: string | null): ConnectResult | undefined {
  return connectResults.find((one) => one === value);
}

function toReason(value: string | null): ConnectReason {
  return connectReasons.find((one) => one === value) ?? 'unknown';
}

interface UseConnectReturnProps {
  /** Called once, after a login came back, so the cards show the new state. */
  onReturn: () => void;
}

/**
 * What the platform said when it sent the browser back.
 *
 * The answer arrives as query params on the Channels screen. It is read once,
 * then stripped from the address, so a reload does not raise the same banner
 * again and a copied link carries no stale result.
 */
export function useConnectReturn({ onReturn }: UseConnectReturnProps) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [outcome, setOutcome] = useState<ConnectOutcome>();
  const isRead = useRef(false);

  useEffect(() => {
    if (isRead.current) {
      return;
    }
    const platform = searchParams.get('promoConnect');
    const result = toResult(searchParams.get('result'));
    if (!isSocialPlatform(platform) || result === undefined) {
      return;
    }
    isRead.current = true;
    setOutcome({ platform, result, reason: toReason(searchParams.get('reason')) });

    const next = new URLSearchParams(searchParams);
    next.delete('promoConnect');
    next.delete('result');
    next.delete('reason');
    setSearchParams(next, { replace: true });
    onReturn();
  }, [onReturn, searchParams, setSearchParams]);

  const dismiss = useCallback(() => setOutcome(undefined), []);

  return { outcome, dismiss };
}
